import { Col, Form, InputNumber, message, Modal, Row, Select } from 'antd';
import {
	useGetMaterialRequestReasonsQuery,
	useRequestMoreMaterialMutation,
} from '../../redux/api/material-request';
import { useEffect, useRef, useState } from 'react';
import { MaterialRequest } from '../../interfaces/Material.Interface';
import { useCalculateScrapQuantityMutation } from '../../redux/api/material';
import { Lineitem, ProductionMethod } from '../../interfaces/Ordel.Interface';
import { ScrapBreakdown } from '../ScrapInfo/ScrapInfoButton';
import ScrapRecommendation from '../ScrapInfo/ScrapRecommendation';
import { getEffectiveShapeName } from '../../utils/shapeUtils';

interface RequestMoreMaterialModalProps {
	visible: boolean;
	setVisible: (value: boolean) => void;
	material_request?: MaterialRequest;
	refetchData: () => void;
}

const RequestAdditionalMaterialModal = (
	props: RequestMoreMaterialModalProps
) => {
	const [form] = Form.useForm();

	const requestReasonsQuery = useGetMaterialRequestReasonsQuery();

	const [requestMoreMaterial] = useRequestMoreMaterialMutation();
	const [calculateScrapQuantity] = useCalculateScrapQuantityMutation();

	const isSwissProduction =
		props.material_request?.order_line_item &&
		typeof props.material_request.order_line_item === 'object' &&
		(props.material_request.order_line_item as Lineitem)
			.partsbadger_production_method === ProductionMethod.Swiss;

	// Rapid edits fire overlapping scrap lookups that can resolve out of order,
	// so only the newest one may write state. Callers get `null` when their
	// lookup was superseded and must leave the form alone; `undefined` means the
	// lookup does not apply (Swiss / no request) and the current value stands.
	const latestScrapRequest = useRef(0);

	const recomputeStandardScrap = async (
		cutQty: number
	): Promise<number | null | undefined> => {
		if (isSwissProduction || !props.material_request) return undefined;
		const requestId = ++latestScrapRequest.current;
		const mr = props.material_request;
		// The material request read serializer (MaterialOrderRequestSerializer)
		// does not expose the top-level shape/type/dimensions; that geometry
		// lives on the selected vendor_material. Reading mr.shape/mr.type/mr.height
		// here returned undefined, so the backend could never resolve a cost per
		// piece and the recommendation always dropped to the smart fallback, even
		// though the original request was costed from the lookup table. Pull the
		// shape, material type and dimensions from vendor_material (falling back to
		// any top-level values) so the lookup table is used (PTB-5965).
		const vm = mr.vendor_material;
		const shapeName =
			getEffectiveShapeName(
				vm?.shape?.display_name ?? mr.shape?.display_name
			) ??
			vm?.shape?.display_name ??
			mr.shape?.display_name ??
			null;
		const materialTypeId = vm?.type?.id ?? mr.type?.id;
		// The lookup table can legitimately recommend 0 for expensive,
		// low-volume jobs. The fallback remains at least 1 for a valid quantity.
		const fallback =
			cutQty >= 1 ? Math.max(Math.round(cutQty * 0.15), 1) : 0;
		try {
			const res = await calculateScrapQuantity({
				quantity: cutQty,
				shape: shapeName,
				material_cut_length: Number(mr.material_cut_length),
				material_type_id: materialTypeId,
				height: Number(vm?.height ?? mr.height) || undefined,
				width: Number(vm?.width ?? mr.width) || undefined,
				outer_diameter:
					Number(vm?.outer_diameter ?? mr.outer_diameter) || undefined,
				inner_diameter:
					Number(vm?.inner_diameter ?? mr.inner_diameter) || undefined,
				wall_thickness:
					Number(vm?.wall_thickness ?? mr.wall_thickness) || undefined,
			}).unwrap();
			if (requestId !== latestScrapRequest.current) {
				return null;
			}
			setScrapBreakdown({ ...(res as ScrapBreakdown), manual: false });
			return Math.max(
				Number(res?.estimated_scrap_quantity ?? fallback) || 0,
				0
			);
		} catch {
			if (requestId !== latestScrapRequest.current) {
				return null;
			}
			setScrapBreakdown({
				quantity: cutQty,
				cost_per_piece: null,
				estimated_scrap_quantity: fallback,
				used_fallback: true,
				fallback_reason: 'cost_per_piece_unavailable',
				fallback_percent: 0.15,
				quantity_tier: null,
				cost_tier: null,
				manual: false,
			});
			return fallback;
		}
	};

	function handleSubmit(id: string, values: Partial<MaterialRequest>) {
		requestMoreMaterial({ id: id, values: values })
			.unwrap()
			.then(() => {
				props.setVisible(false);
				props.refetchData();
			})
			.catch(() => message.error('Failed to create Material Request'));
	}

	const [materialCutLength, setMaterialCutLength] = useState<string>();

	const [estimatedScrapQuantity, setEstimatedScrapQuantity] =
		useState<number>(0);
	const [cutQuantity, setCutQuantity] = useState<number>(1);
	const [scrapBreakdown, setScrapBreakdown] =
		useState<ScrapBreakdown | null>(null);

	const handleMaterialCutQuantityChange = async (value: number) => {
		setCutQuantity(value);
		const result = await recomputeStandardScrap(value);
		if (result === null) {
			return;
		}
		const scrap = result ?? estimatedScrapQuantity;
		setEstimatedScrapQuantity(scrap);
		form.setFieldValue('estimated_scrap_quantity', scrap);
		const requiredLength = calculateRequiredMaterialLength(
			Number(materialCutLength),
			0.07,
			scrap + value
		);
		form.setFieldValue('total_required_length', requiredLength);
	};

	const handleMaterialScrapQuantityChange = (value: number) => {
		// Editable field; leave the recommendation (scrapBreakdown) untouched.
		setEstimatedScrapQuantity(value);
		form.setFieldValue('estimated_scrap_quantity', value);
		const requiredLength = calculateRequiredMaterialLength(
			Number(materialCutLength),
			0.07,
			cutQuantity + value
		);
		form.setFieldValue('total_required_length', requiredLength);
	};

	function calculateRequiredMaterialLength(
		materialCutLength: number,
		sawKerf: number,
		materialCutQuantity: number
	) {
		return Number(
			Number((materialCutLength + sawKerf) * materialCutQuantity).toFixed(2)
		);
	}

	useEffect(() => {
		if (props.material_request) {
			const mr = props.material_request;
			setMaterialCutLength(mr.material_cut_length);
			setCutQuantity(mr.material_cut_quantity);

			(async () => {
				const result = await recomputeStandardScrap(
					mr.material_cut_quantity
				);
				if (result === null) {
					return;
				}
				const scrap = result ?? mr.estimated_scrap_quantity;
				setEstimatedScrapQuantity(scrap);
				form.setFieldValue('estimated_scrap_quantity', scrap);
				form.setFieldValue('material_cut_quantity', mr.material_cut_quantity);
				form.setFieldValue(
					'total_required_length',
					calculateRequiredMaterialLength(
						Number(mr.material_cut_length),
						0.07,
						scrap + mr.material_cut_quantity
					)
				);
			})();
		}
	}, [props.material_request]);

	return (
		<>
			<Modal
				title="Request More Material"
				open={props.visible}
				centered={true}
				onCancel={() => props.setVisible(false)}
				okButtonProps={{
					onClick: async () => {
						const values = await form.validateFields();

						if (props.material_request && props.material_request.id) {
							handleSubmit(props.material_request.id, {
								material_cut_quantity: values.material_cut_quantity,
								estimated_scrap_quantity: values.estimated_scrap_quantity,
								total_required_length: values.total_required_length,
								additional_request_reason: values.additional_request_reason,
							});
						}
					},
				}}
			>
				<Form form={form} layout="vertical">
					<Row>
						<Col span={24}>
							<Form.Item
								name="material_cut_quantity"
								label="Cut Quantity"
								rules={[
									{
										required: true,
										message: 'Please input the cut quantity',
									},
								]}
							>
								<InputNumber
									placeholder="Cut Quantity"
									step={1}
									min={1}
									value={cutQuantity}
									onStep={(value) => handleMaterialCutQuantityChange(value)}
									onBlur={(e) =>
										handleMaterialCutQuantityChange(Number(e.target.value))
									}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								name="estimated_scrap_quantity"
								label="Estimated Scrap Quantity"
								rules={[
									{
										required: true,
										message: 'Please input the scrap quantity',
									},
								]}
								style={{ marginBottom: 0 }}
							>
								<InputNumber
									placeholder="Estimated Scrap Quantity"
									step={1}
									min={0}
									value={estimatedScrapQuantity}
									onStep={(value) => handleMaterialScrapQuantityChange(value)}
									onBlur={(e) =>
										handleMaterialScrapQuantityChange(Number(e.target.value))
									}
									style={{ width: '100%' }}
								/>
							</Form.Item>
							{!isSwissProduction && (
								<ScrapRecommendation
									breakdown={scrapBreakdown}
									onApply={handleMaterialScrapQuantityChange}
								/>
							)}
						</Col>

						<Col span={24}>
							<Form.Item
								name="total_required_length"
								label="Total Required Length"
							>
								<InputNumber
									placeholder="Total Required Length"
									step={1}
									min={1}
									style={{ width: '100%' }}
									readOnly={true}
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								name="additional_request_reason"
								label="Reason for request"
								rules={[
									{
										required: true,
										message: 'Please select a reason',
									},
								]}
							>
								<Select
									options={requestReasonsQuery?.data?.map((x) => {
										return { value: x.id, label: x.name };
									})}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	);
};

export default RequestAdditionalMaterialModal;
